import * as pdfjsLib from "pdfjs-dist";
import { PDFDocument, degrees } from "pdf-lib";
import { Document, Packer, Paragraph, TextRun } from "docx";
import { canvasToBlob } from "./utils.js";

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL("./assets/pdf.worker.min.mjs", document.baseURI).href;

export async function loadPdf(arrayBuffer) {
  const task = pdfjsLib.getDocument({ data: new Uint8Array(arrayBuffer.slice(0)) });
  return task.promise;
}

export async function renderPage(pdf, pageNumber, scale = 1.5) {
  const page = await pdf.getPage(pageNumber);
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement("canvas");
  canvas.width = Math.floor(viewport.width);
  canvas.height = Math.floor(viewport.height);
  const ctx = canvas.getContext("2d");
  await page.render({ canvasContext: ctx, viewport }).promise;
  return canvas;
}

export async function renderPages(pdf, scale, onProgress) {
  const canvases = [];
  for (let i = 1; i <= pdf.numPages; i += 1) {
    canvases.push(await renderPage(pdf, i, scale));
    onProgress?.(i, pdf.numPages);
  }
  return canvases;
}

export async function pdfToImages(pdf, { format = "png", scale = 2, pages }) {
  const targets = pages || Array.from({ length: pdf.numPages }, (_, i) => i + 1);
  const type = format === "jpg" ? "image/jpeg" : "image/png";
  const quality = format === "jpg" ? 0.92 : undefined;
  const out = [];
  for (const pageNumber of targets) {
    const canvas = await renderPage(pdf, pageNumber, scale);
    out.push({ page: pageNumber, blob: await canvasToBlob(canvas, type, quality) });
  }
  return out;
}

export async function extractPdfText(pdf) {
  const parts = [];
  for (let i = 1; i <= pdf.numPages; i += 1) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    const text = content.items
      .map((item) => (typeof item.str === "string" ? item.str : ""))
      .join(" ");
    parts.push(`第 ${i} 页\n${text}`);
  }
  return parts.join("\n\n");
}

export async function pdfToDocxBlob(pdf) {
  const text = await extractPdfText(pdf);
  const paragraphs = text
    .split(/\n+/)
    .map((line) => new Paragraph({ children: [new TextRun(line.trim() || " ")] }));
  const doc = new Document({ sections: [{ children: paragraphs }] });
  return Packer.toBlob(doc);
}

export async function compressPdf(arrayBuffer, { quality = 0.72, scale = 1.4, maxWidth = 1500 } = {}) {
  const src = await loadPdf(arrayBuffer);
  const out = await PDFDocument.create();
  for (let i = 1; i <= src.numPages; i += 1) {
    const page = await src.getPage(i);
    const base = page.getViewport({ scale: 1 });
    const effectiveScale = Math.min(scale, maxWidth / Math.max(1, base.width));
    const canvas = await renderPage(src, i, Math.max(0.5, effectiveScale));
    const jpeg = await canvasToBlob(canvas, "image/jpeg", quality);
    const img = await out.embedJpg(new Uint8Array(await jpeg.arrayBuffer()));
    const outPage = out.addPage([img.width, img.height]);
    outPage.drawImage(img, { x: 0, y: 0, width: img.width, height: img.height });
  }
  return out.save({ useObjectStreams: true });
}

export async function editPdfPages(arrayBuffer, { order, rotations, deleted }) {
  const src = await PDFDocument.load(arrayBuffer);
  const out = await PDFDocument.create();
  const indices = (order || Array.from({ length: src.getPageCount() }, (_, i) => i)).filter(
    (i) => !deleted?.has(i),
  );
  for (const idx of indices) {
    const [page] = await out.copyPages(src, [idx]);
    const rot = rotations?.[idx] || 0;
    if (rot) page.setRotation(degrees(((page.getRotation().angle || 0) + rot) % 360));
    out.addPage(page);
  }
  return out.save({ useObjectStreams: true });
}

export async function mergePdfs(buffers) {
  const out = await PDFDocument.create();
  for (const buffer of buffers) {
    const src = await PDFDocument.load(buffer);
    const pages = await out.copyPages(src, src.getPageIndices());
    pages.forEach((page) => out.addPage(page));
  }
  return out.save({ useObjectStreams: true });
}

export function pdfBufferToBlob(buffer) {
  return new Blob([buffer], { type: "application/pdf" });
}
